// One past ticket, opened over the conversation it was matched against.

import { useEffect, useState } from 'react';
import { CircleAlert, ExternalLink, LoaderCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MarkdownContent } from '@/components/markdown-content';
import { cn } from '@/lib/utils';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { MessageAvatar } from './chat-thread';
import { timeLabel } from './sentinel-workspace.helpers';
import { ScoreChip } from './similar-tickets-card';
import type { SimilarTicketMatch, SimilarTicketThreadDto } from './types';

// Row geometry from the design: customer rows keep 96px free on the right, agent rows 93px on the left.
const CUSTOMER_ROW = 'flex items-start gap-2.5 pl-4 pr-24 pt-4';
const AGENT_ROW = 'flex items-end justify-end gap-[7px] pl-[93px] pr-4 pt-4';
// Same border, fill, shadow and w-fit as the live thread's bubbles, so the two read as one surface.
const CUSTOMER_BUBBLE = 'min-w-0 w-fit rounded-2xl border border-gray-300 bg-white p-4 shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]';
const AGENT_BUBBLE = 'min-w-0 w-fit rounded-2xl border border-lavender-100 bg-lavender-50 p-4';
const TEXT = 'text-[12px] leading-normal text-text-quote [&_p]:mb-2 [&_p:last-child]:mb-0';

// The load failed — most often a match whose conversation carries no external id.
// `reason` is the API's own message, which names the cause far better than a fixed line can.
function ThreadError({ reason }: { reason: string }) {
  return (
    <div className="p-4">
      <div className="flex w-full items-center gap-3 rounded-2xl border border-red-300 bg-red-100 p-3">
        <CircleAlert className="size-5 shrink-0 text-red-500" />
        <div className="flex min-w-0 flex-col">
          <p className="text-[13px] leading-[18px] text-red-500">Could not load this conversation.</p>
          <p className="text-[12px] leading-4 text-gray-900">{reason}</p>
        </div>
      </div>
    </div>
  );
}

// The thread lands in one go, so it waits on a single line rather than a mock of itself.
function ThreadLoading() {
  return (
    <div className="flex items-center justify-center gap-2.5 px-4 pt-6">
      <LoaderCircle className="size-[14px] animate-spin text-gray-900" />
      <p className="text-[13px] leading-[18px] text-text-body">Loading conversation</p>
    </div>
  );
}

export function ThreadBubble({
  role,
  text,
  createdAt,
  textClassName,
  bubbleClassName,
}: SimilarTicketThreadDto['messages'][number] & { textClassName?: string; bubbleClassName?: string }) {
  if (role === 'agent') {
    return (
      <div className={AGENT_ROW}>
        <div className={cn(AGENT_BUBBLE, 'flex flex-col gap-1.5', bubbleClassName)}>
          <MarkdownContent text={text} className={cn(TEXT, textClassName)} />
          <p className="text-right text-[10px] leading-normal text-gray-800">{timeLabel(createdAt)}</p>
        </div>
        <MessageAvatar name="Agent" />
      </div>
    );
  }
  return (
    <div className={CUSTOMER_ROW}>
      <MessageAvatar name="Customer" />
      <div className={cn(CUSTOMER_BUBBLE, bubbleClassName)}>
        <MarkdownContent text={text} className={cn(TEXT, textClassName)} />
      </div>
    </div>
  );
}

// Sits over the right of the conversation pane, so the live thread stays readable beside it.
export function SimilarTicketPanel({ ticketId, match, onClose }: {
  ticketId: number;
  match: SimilarTicketMatch;
  onClose: () => void;
}) {
  const [thread, setThread] = useState<SimilarTicketThreadDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setThread(null);
    setError(null);
    setLoading(true);
    sentinelWorkspaceService
      .getSimilarTicketThread(ticketId, match.conversationId)
      .then((next) => live && setThread(next))
      // api.ts rewrites `message` to the API's own, so this is the backend's reason when it sent one.
      .catch((reason: unknown) => {
        if (!live) return;
        const message = reason instanceof Error ? reason.message.trim() : '';
        setError(message || 'Something went wrong. Please try again in a moment.');
      })
      .finally(() => live && setLoading(false));
    // A ticket opened later must not be overwritten by an earlier request landing late.
    return () => {
      live = false;
    };
  }, [ticketId, match.conversationId]);

  // Escape closes it, like every other overlay in the workspace.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // The blur is forced: a global [role='dialog'] rule sets 22px, which flattens the glass.
    // Inset 16px like the composer's p-4, so both sit on the pane's right and bottom edges.
    <div
      role="dialog"
      aria-label={`Similar ticket #${match.conversationId}`}
      className="absolute inset-y-4 right-4 z-30 flex w-[433px] max-w-[calc(100%-2rem)] flex-col overflow-hidden rounded-[20px] border border-gray-500 bg-white/80 shadow-[0_3px_12px_0_rgba(0,0,0,0.08)] backdrop-blur-[6px]! animate-in fade-in-0 slide-in-from-right-4 duration-200 ease-out motion-reduce:animate-none"
    >
      <div className="flex shrink-0 items-start justify-between px-3 py-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 flex-col">
            <p className="truncate text-[16px] font-medium leading-6 text-text-body">#{match.conversationId}</p>
            {thread?.subject && <p className="truncate text-[11px] leading-4 text-gray-900">{thread.subject}</p>}
          </div>
          <div className="flex items-center gap-1">
            {thread?.category && (
              <span className="flex items-center gap-2 rounded-[6px] border border-gray-400 bg-white px-3 py-1 text-[10px] font-medium leading-[1.1] text-accent-purple">
                <span className="size-1.5 shrink-0 rounded-[1px] bg-accent-purple" />
                {thread.category}
              </span>
            )}
            <ScoreChip similarity={match.similarity} />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {/* The divider belongs to the link: without one, only the close button remains. */}
          {thread?.ticketUrl && (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                asChild
                className="rounded-full text-gray-900"
                aria-label={`Open #${match.conversationId} in the helpdesk`}
              >
                <a href={thread.ticketUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="size-[14px]" />
                </a>
              </Button>
              <span className="h-3 w-px bg-gray-500" />
            </>
          )}
          <Button variant="ghost" size="icon-sm" onClick={onClose} className="rounded-full text-gray-900" aria-label="Close ticket">
            <X className="size-[14px]" />
          </Button>
        </div>
      </div>

      <div className="h-px w-full shrink-0 bg-gray-300" />

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">
        {loading && <ThreadLoading />}
        {error && <ThreadError reason={error} />}
        {!loading && !error && thread?.messages.length === 0 && (
          <span className="px-4 pt-4 text-[12px] leading-4 text-text-disabled">No messages on this ticket.</span>
        )}
        {thread?.messages.map((message) => <ThreadBubble key={message.id} {...message} />)}
      </div>
    </div>
  );
}
